import styled from '@emotion/styled';
import { Button } from '@mantine/core';
import { useCallback, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch } from '../../store';
import { Page } from '../page';
import { useOption } from '../../core/options/use-option';
import { isProxySupported } from '../../core/chat/openai';
import { HARDCODED_VALUES } from '../../hardcoded-values';

const Messages = styled.div`
    @media (min-height: 30em) {
        max-height: 100%;
        flex-grow: 1;
        overflow-y: scroll;
    }
    display: flex;
    flex-direction: column;
`;

const WelcomeCard = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: linear-gradient(135deg, #292933 0%, #1a1a23 100%);
    color: #fffbe7;
    padding: 2rem 2.5rem;
    border-radius: 1rem;
    margin: 1.5rem 0 1.5rem 0;
    max-width: 650px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    border: 1.5px solid #f09819;
    letter-spacing: 0.01em;
    text-align: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, #f09819, #ff6b6b, #f09819);
        animation: shimmer 3s ease-in-out infinite;
    }

    @keyframes shimmer {
        0%, 100% { transform: translateX(-100%); }
        50% { transform: translateX(100%); }
    }
`;

const DiscordLink = styled.a`
    color: #7289da;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
        color: #5865f2;
        text-decoration: underline;
    }
`;

const DiscordIcon = styled.a`
    color: #7289da;
    font-size: 1.1rem;
    text-decoration: none;
    transition: all 0.3s ease;
    margin-left: 0.3rem;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    line-height: 1;

    &:hover {
        color: #5865f2;
        transform: scale(1.1);
    }

    &:active {
        transform: scale(0.95);
    }
`;

const Greeting = styled.h2`
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 1.5rem 0;
    background: linear-gradient(45deg, #f09819, #ff6b6b);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Instructions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    text-align: left;
    width: 100%;
`;

const InstructionStep = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    font-size: 0.95rem;
    line-height: 1.8;
    color: #e8e8e8;
`;

const StepNumber = styled.div`
    background: linear-gradient(45deg, #f09819, #ff6b6b);
    color: white;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 0.1rem;
`;

const StepText = styled.span`
    flex: 1;
`;

const ProxyStatus = styled.p`
    margin-top: 2.5rem;
    color: #ffb347;
    font-size: 1rem;
    text-align: center;
    max-width: 600px;
`;

const LandingContent = styled.div`
    flex-grow: 1;
    padding-bottom: 5vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-family: "Work Sans", sans-serif;
    line-height: 1.7;
    gap: 1rem;
`;

// Random greeting system
const GREETINGS = [ "Want the Epstein logs? I might have em.",
    "I can prove jet fuel melts steel beams!",
    "Kiss the wall, fuck the wall.",
    "THE ADL WILL NEVER CATCH ME",
    "Why were there dancing Israelis on 9/11? Let's discuss.",
    "There's a certain 'group' you can't criticize. Let's criticize them.",
    "Why were the mattresses stained?",
];

export default function LandingPage(props: any) {
    const dispatch = useAppDispatch();
    const [currentGreetingIndex] = useState(() => 
        Math.floor(Math.random() * GREETINGS.length)
    );

    return <Page id={'landing'} showSubHeader={true}>
        <Messages id="messages">
            <LandingContent>
                <WelcomeCard>
                    <Greeting>
                        {GREETINGS[currentGreetingIndex]}
                    </Greeting>
                    <Instructions>
                        <InstructionStep>
                            <StepNumber>1</StepNumber>
                            <StepText>
                                <FormattedMessage
                                    defaultMessage="Send a message below to begin a new conversation"
                                    description="First instruction step"
                                />
                            </StepText>
                        </InstructionStep>
                        <InstructionStep>
                            <StepNumber>2</StepNumber>
                            <StepText>
                                <FormattedMessage
                                    defaultMessage="Adjust personality and model by clicking the gear icon in the top right."
                                    description="Second instruction step"
                                />
                            </StepText>
                        </InstructionStep>
                        <InstructionStep>
                            <StepNumber>3</StepNumber>
                            <StepText>
                                <FormattedMessage
                                    defaultMessage="Turn ON political extremism in settings at your discretion."
                                    description="Third instruction step"
                                />
                            </StepText>
                        </InstructionStep>
                        <InstructionStep>
                            <StepNumber>4</StepNumber>
                            <StepText>
                                <span>Join Our </span>
                                <DiscordLink 
                                    href={HARDCODED_VALUES.discord.inviteLink}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                >
                                    Discord
                                </DiscordLink>
                                <DiscordIcon 
                                    href={HARDCODED_VALUES.discord.inviteLink}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    title="Join our Discord"
                                >
                                    <i className="fab fa-discord" />
                                </DiscordIcon>
                            </StepText>
                        </InstructionStep>
                        <InstructionStep>
                            <StepNumber>5</StepNumber>
                            <StepText>
                                The real URL is: <a href="https://basedgpt.dev" style={{ color: '#f09819', fontWeight: 600 }}>https://basedgpt.dev</a>
                            </StepText>
                        </InstructionStep>
                        <InstructionStep>
                            <StepNumber>6</StepNumber>
                            <StepText>
                                Also, check out <a href="https://naggers.net" style={{ color: '#f09819', fontWeight: 600 }}>https://naggers.net</a> for great free information!
                            </StepText>
                        </InstructionStep>
                    </Instructions>
                </WelcomeCard>
                {!isProxySupported() && (
                    <ProxyStatus>
                        <FormattedMessage defaultMessage={'BasedGPT may currently be experiencing outages. Emphasis on the "may", it is still probably working fine.'} />
                    </ProxyStatus>
                )}
            </LandingContent>
        </Messages>
    </Page>;
}
